import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface TourStep {
  id: string;
  title: string;
  content: string;
  target?: string; // CSS selector for the element to highlight
  position: 'top' | 'bottom' | 'left' | 'right' | 'center' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  action?: string; // Optional action button text
  canSkip?: boolean;
}

export interface TourState {
  isActive: boolean;
  currentStepIndex: number;
  totalSteps: number;
  currentStep?: TourStep;
}

@Injectable({
  providedIn: 'root'
})
export class TourService {
  private tourStateSubject = new BehaviorSubject<TourState>({
    isActive: false,
    currentStepIndex: 0,
    totalSteps: 0
  });

  public tourState$: Observable<TourState> = this.tourStateSubject.asObservable();
  private tourSteps: TourStep[] = [];

  constructor() {}

  /**
   * Start a tour with the provided steps
   */
  startTour(steps: TourStep[]): void {
    this.tourSteps = steps;
    this.tourStateSubject.next({
      isActive: true,
      currentStepIndex: 0,
      totalSteps: steps.length,
      currentStep: steps[0]
    });
  }

  /**
   * Go to the next step in the tour
   */
  nextStep(): void {
    const currentState = this.tourStateSubject.value;
    if (!currentState.isActive) return;

    const nextIndex = currentState.currentStepIndex + 1;
    
    if (nextIndex < this.tourSteps.length) {
      this.tourStateSubject.next({
        ...currentState,
        currentStepIndex: nextIndex,
        currentStep: this.tourSteps[nextIndex]
      });
    } else {
      this.endTour();
    }
  }

  /**
   * Go to the previous step in the tour
   */
  previousStep(): void {
    const currentState = this.tourStateSubject.value;
    if (!currentState.isActive || currentState.currentStepIndex === 0) return;

    const prevIndex = currentState.currentStepIndex - 1;
    this.tourStateSubject.next({
      ...currentState,
      currentStepIndex: prevIndex,
      currentStep: this.tourSteps[prevIndex]
    });
  }

  /**
   * Skip to a specific step
   */
  goToStep(index: number): void {
    const currentState = this.tourStateSubject.value;
    if (!currentState.isActive || index < 0 || index >= this.tourSteps.length) return;

    this.tourStateSubject.next({
      ...currentState,
      currentStepIndex: index,
      currentStep: this.tourSteps[index]
    });
  }

  /**
   * End the tour
   */
  endTour(): void {
    this.tourStateSubject.next({
      isActive: false,
      currentStepIndex: 0,
      totalSteps: 0,
      currentStep: undefined
    });
    this.tourSteps = [];
  }

  /**
   * Check if tour is currently active
   */
  isTourActive(): boolean {
    return this.tourStateSubject.value.isActive;
  }

  /**
   * Get current tour state
   */
  getCurrentState(): TourState {
    return this.tourStateSubject.value;
  }
}

